import React, { useReducer, useMemo, useEffect, useContext, useCallback } from 'react';
import { useDebouncedCallback } from 'use-debounce';
import useMessagingSubscription from '@components/attendeeMessaging/useMessagingSubscription';
import { Channel, StreamChat } from 'stream-chat';
import { Attendee } from '@graphql/gql/graphql';
import { StreamType } from '@components/chat/shared/useStreamChat';
import objectHasProperty from '@tools/objectHasProperty';
import { FormattedError, useStreamClient } from './StreamClientContext';

interface AttendeeMessagingContext {
  unreadCount: number;
  channels: Record<string, Channel>;
  hasMoreChannels: boolean;
  offset: number;
  currentChannel: null | Channel;
  otherAttendees: Map<string, Attendee>;
  channelAttendees: string[];
}

export const MESSAGING_REDUCER_ACTIONS = Object.freeze({
  UPDATE_CHANNELS: 'UPDATE_CHANNELS',
  UPDATE_UNREAD_COUNT: 'UPDATE_UNREAD_COUNT',
  SET_CURRENT_CHANNEL: 'SET_CURRENT_CHANNEL',
  UPDATE_OTHER_ATTENDEES: 'UPDATE_OTHER_ATTENDEES',
  SET_CHANNEL_ATTENDEES: 'SET_CHANNEL_ATTENDEES'
});

const initialState = {
  unreadCount: 0,
  channels: {},
  hasMoreChannels: true,
  offset: 0,
  currentChannel: null,
  otherAttendees: new Map(),
  channelAttendees: []
};

const reducer = (state, action) => {
  switch (action.type) {
    case MESSAGING_REDUCER_ACTIONS.UPDATE_CHANNELS: {
      const { channels, hasMoreChannels, offset } = action.payload;

      return {
        ...state,
        hasMoreChannels,
        offset,
        channels: { ...state.channels, ...channels }
      };
    }
    case MESSAGING_REDUCER_ACTIONS.UPDATE_UNREAD_COUNT: {
      return {
        ...state,
        unreadCount: action.payload.unreadCount
      };
    }
    case MESSAGING_REDUCER_ACTIONS.SET_CURRENT_CHANNEL: {
      // Stay on the channel the user has switched to,
      // after the new channel has been created.
      if (state.currentChannel && action.payload.isNewChannel) {
        return state;
      }
      return {
        ...state,
        currentChannel: action.payload.currentChannel
      };
    }
    case MESSAGING_REDUCER_ACTIONS.UPDATE_OTHER_ATTENDEES: {
      const { otherAttendees } = action.payload;
      if (otherAttendees.length === 0) {
        return state;
      }
      const otherAttendeesMap = otherAttendees.map(attendee => [attendee.id, attendee]);
      const newOtherAttendees = new Map([...state.otherAttendees, ...otherAttendeesMap]);
      return {
        ...state,
        otherAttendees: newOtherAttendees
      };
    }
    case MESSAGING_REDUCER_ACTIONS.SET_CHANNEL_ATTENDEES: {
      return {
        ...state,
        channelAttendees: action.payload.channelAttendees
      };
    }
    default:
      return state;
  }
};

export type AttendeeMessagingContextType = {
  unreadCount?: number;
  channels?: Record<string, Channel<StreamType>>;
  hasMoreChannels?: boolean;
  offset?: number;
  currentChannel?: Channel<StreamType>;
  otherAttendees?: Map<string, Attendee>;
  channelAttendees?: Attendee[];
  setCurrentChannel?: ({
    currentChannel,
    isNewChannel
  }: {
    currentChannel: Channel<StreamType> | null;
    isNewChannel?: boolean;
  }) => void;
  getAttendeeData?: (attendeeId: string) => Attendee | null;
  updateOtherAttendees?: ({ otherAttendees }: { otherAttendees: Partial<Attendee>[] }) => void;
  setChannelAttendees?: ({ channelAttendees }: { channelAttendees: Attendee[] }) => void;
  updateChannels?: ({
    channels,
    hasMoreChannels,
    offset
  }: {
    channels: { [key: string]: Channel<StreamType> };
    hasMoreChannels?: boolean;
    offset?: number;
  }) => void;
  client?: StreamChat<StreamType>;
  getClient?: (isRetry?: boolean) => Promise<{ client?: StreamChat<StreamType>; error?: FormattedError }>;
  streamClientError?: Error;
};

const AttendeeMessagingContext = React.createContext<AttendeeMessagingContextType>(initialState);

const useAttendeeMessaging = (): AttendeeMessagingContextType => {
  const streamClient = useStreamClient();
  const { otherAttendees, ...rest } = useContext(AttendeeMessagingContext);
  const getAttendeeData = useCallback<(attendeeId: string) => Attendee | null>(
    attendeeId => {
      if (otherAttendees.size === 0) return null;
      return otherAttendees.get(attendeeId);
    },
    [otherAttendees]
  );
  return {
    ...streamClient,
    ...rest,
    otherAttendees,
    getAttendeeData
  };
};

function AttendeeMessagingProvider({ children }: { children?: React.ReactNode }) {
  const { client, getClient, updateConnectionStatus } = useStreamClient();
  const [state, dispatch] = useReducer(reducer, {
    ...initialState
  });

  const updateChannels = useCallback(
    payload => {
      dispatch({
        type: MESSAGING_REDUCER_ACTIONS.UPDATE_CHANNELS,
        payload
      });
    },
    [dispatch]
  );

  const debouncedUpdateUnreadCount = useDebouncedCallback(
    unread =>
      dispatch({
        type: MESSAGING_REDUCER_ACTIONS.UPDATE_UNREAD_COUNT,
        payload: { unreadCount: unread }
      }),
    100
  );

  const setCurrentChannel = useCallback(
    payload => {
      dispatch({
        type: MESSAGING_REDUCER_ACTIONS.SET_CURRENT_CHANNEL,
        payload
      });
    },
    [dispatch]
  );

  const updateOtherAttendees = useCallback(
    payload => {
      dispatch({
        type: MESSAGING_REDUCER_ACTIONS.UPDATE_OTHER_ATTENDEES,
        payload
      });
    },
    [dispatch]
  );

  const setChannelAttendees = useCallback(
    payload => {
      dispatch({
        type: MESSAGING_REDUCER_ACTIONS.SET_CHANNEL_ATTENDEES,
        payload
      });
    },
    [dispatch]
  );

  useEffect(() => {
    if (!client) return () => null;

    debouncedUpdateUnreadCount(
      objectHasProperty(client?.user, 'total_unread_count') ? client?.user?.total_unread_count : undefined
    );

    const eventHandler = event => {
      if (event.total_unread_count !== undefined) {
        debouncedUpdateUnreadCount(event.total_unread_count);
      }
    };

    client.on(eventHandler);

    return () => {
      client.off(eventHandler);
    };
  }, [client, debouncedUpdateUnreadCount]);

  const { hasChannels, loading } = useMessagingSubscription({
    getClient,
    client
  });

  useEffect(() => {
    // don't updateConnectionStatus until MeQuery data is available so that we have true value of hasChannels
    if (!loading) {
      updateConnectionStatus({
        keepConnectionAlive: hasChannels
      });
    }
  }, [hasChannels, updateConnectionStatus, loading]);

  const contextValue = useMemo(
    () => ({
      ...state,
      updateChannels,
      setCurrentChannel,
      updateOtherAttendees,
      setChannelAttendees
    }),
    [state, updateChannels, setCurrentChannel, updateOtherAttendees, setChannelAttendees]
  );

  return <AttendeeMessagingContext.Provider value={contextValue}>{children}</AttendeeMessagingContext.Provider>;
}

export { AttendeeMessagingContext, AttendeeMessagingProvider, useAttendeeMessaging };
